// ============================================================================
// PHASE LIBRARY SELECTOR COMPONENT
// Searchable phase list with ordering controls (Step 2 of wizard)
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, ChevronUp, ChevronDown, Trash2, Layers, Info } from 'lucide-react';
import { PhaseTemplate, PhaseReference, PhaseCategory } from '../../types';
import { browsePhaseTemplates } from '../../services/templateService';

interface PhaseLibrarySelectorProps {
  selectedPhases: PhaseReference[];
  onChange: (phases: PhaseReference[]) => void;
}

export default function PhaseLibrarySelector({ selectedPhases, onChange }: PhaseLibrarySelectorProps) {
  const [availablePhases, setAvailablePhases] = useState<PhaseTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<PhaseCategory | ''>('');

  const loadPhases = useCallback(async () => {
    try {
      setLoading(true);
      const result = await browsePhaseTemplates({
        category: selectedCategory || undefined,
        search: searchTerm || undefined,
        limit: 50
      });
      setAvailablePhases(result.templates);
    } catch (error) {
      console.error('Failed to load phases:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchTerm]);

  useEffect(() => {
    setSearchTerm('');
    loadPhases();
  }, [selectedCategory, loadPhases]);

  const handleSearch = () => {
    loadPhases();
  };

  const addPhase = (phase: PhaseTemplate) => {
    const newPhase: PhaseReference = {
      phaseTemplateId: phase.phaseTemplateId,
      versionNumber: phase.versionNumber,
      orderIndex: selectedPhases.length
    };
    onChange([...selectedPhases, newPhase]);
  };

  const removePhase = (index: number) => {
    const updated = selectedPhases.filter((_, i) => i !== index);
    const reordered = updated.map((phase, i) => ({ ...phase, orderIndex: i }));
    onChange(reordered);
  };

  const movePhaseUp = (index: number) => {
    if (index === 0) return;
    const updated = [...selectedPhases];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    const reordered = updated.map((phase, i) => ({ ...phase, orderIndex: i }));
    onChange(reordered);
  };

  const movePhaseDown = (index: number) => {
    if (index === selectedPhases.length - 1) return;
    const updated = [...selectedPhases];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    const reordered = updated.map((phase, i) => ({ ...phase, orderIndex: i }));
    onChange(reordered);
  };

  const isPhaseSelected = (phaseTemplateId: string) => {
    return selectedPhases.some(p => p.phaseTemplateId === phaseTemplateId);
  };

  const getPhaseDetails = (phaseRef: PhaseReference): PhaseTemplate | undefined => {
    return availablePhases.find(p =>
      p.phaseTemplateId === phaseRef.phaseTemplateId &&
      p.versionNumber === phaseRef.versionNumber
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Phase Library (Left) */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Layers className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-main">Phase Library</h3>
            <p className="text-sm text-text-sub">Browse and add phases to your template.</p>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search phases..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11 text-sm"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 h-11 bg-navy text-white rounded-lg hover:bg-navy-dark transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <Search className="w-4 h-4" />
            </button>
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value as PhaseCategory | '')}
            className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11 text-sm"
          >
            <option value="">All Categories</option>
            <option value={PhaseCategory.KICKOFF}>Kickoff</option>
            <option value={PhaseCategory.DISCOVERY}>Discovery</option>
            <option value={PhaseCategory.IDEATION}>Ideation</option>
            <option value={PhaseCategory.RESEARCH}>Research</option>
            <option value={PhaseCategory.ANALYSIS}>Analysis</option>
            <option value={PhaseCategory.WRAP_UP}>Wrap Up</option>
            <option value={PhaseCategory.CUSTOM}>Custom</option>
          </select>
        </div>

        {/* Phase List */}
        <div className="max-h-[460px] overflow-y-auto space-y-2">
          {loading ? (
            <div className="text-center py-8 text-text-sub text-sm">
              Loading phases...
            </div>
          ) : availablePhases.length === 0 ? (
            <div className="text-center py-8 text-text-sub text-sm">
              No phases found
            </div>
          ) : (
            availablePhases.map(phase => {
              const selected = isPhaseSelected(phase.phaseTemplateId);
              return (
                <div
                  key={`${phase.phaseTemplateId}-${phase.versionNumber}`}
                  className={`rounded-lg border p-3 transition-all ${
                    selected
                      ? 'border-primary/30 bg-primary/5 opacity-60'
                      : 'border-slate-200 bg-white hover:border-primary/50'
                  }`}
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-text-main truncate">
                        {phase.name}
                      </h4>
                      <div className="flex items-center gap-2 mt-1 mb-1.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                          {phase.category}
                        </span>
                        <span className="text-xs text-text-sub">{phase.duration}</span>
                      </div>
                      <p className="text-xs text-text-sub leading-relaxed line-clamp-2">
                        {phase.description}
                      </p>
                    </div>
                    <button
                      onClick={() => addPhase(phase)}
                      disabled={selected}
                      className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                        selected
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'bg-primary text-white hover:bg-primary/90'
                      }`}
                      title={selected ? 'Already added' : 'Add phase'}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Selected Phases (Right) */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Layers className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-main">Selected Phases</h3>
              <p className="text-sm text-text-sub">Arrange the phase order for your template.</p>
            </div>
          </div>
          {selectedPhases.length > 0 && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
              {selectedPhases.length} selected
            </span>
          )}
        </div>

        <div className={`min-h-[460px] rounded-lg border-2 border-dashed p-3 ${
          selectedPhases.length === 0 ? 'border-slate-200 bg-slate-50' : 'border-slate-200 bg-white'
        }`}>
          {selectedPhases.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[400px] text-text-sub">
              <Layers className="w-10 h-10 text-slate-300 mb-3" />
              <p className="font-medium">No phases selected yet</p>
              <p className="text-sm mt-1">Add phases from the library to build your template</p>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedPhases.map((phaseRef, index) => {
                const phase = getPhaseDetails(phaseRef);

                return (
                  <div
                    key={`selected-${phaseRef.phaseTemplateId}-${index}`}
                    className="rounded-lg border border-slate-200 bg-white p-3 hover:border-slate-300 transition-colors"
                  >
                    <div className="flex gap-3 items-center">
                      {/* Order Number */}
                      <div className="w-7 h-7 rounded-md bg-primary text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                        {index + 1}
                      </div>

                      {/* Phase Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-text-main truncate">
                          {phase?.name || `Phase ${phaseRef.phaseTemplateId}`}
                        </h4>
                        <p className="text-xs text-text-sub">
                          {phase?.duration || `v${phaseRef.versionNumber}`}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={() => movePhaseUp(index)}
                          disabled={index === 0}
                          className={`p-1.5 rounded-md transition-colors ${
                            index === 0
                              ? 'text-slate-300 cursor-not-allowed'
                              : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                          }`}
                          title="Move up"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => movePhaseDown(index)}
                          disabled={index === selectedPhases.length - 1}
                          className={`p-1.5 rounded-md transition-colors ${
                            index === selectedPhases.length - 1
                              ? 'text-slate-300 cursor-not-allowed'
                              : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                          }`}
                          title="Move down"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removePhase(index)}
                          className="p-1.5 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selectedPhases.length > 0 && (
          <div className="flex items-start gap-3 bg-primary/10 p-4 rounded-lg border border-primary/20 mt-4">
            <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-sm text-primary/90">
              {selectedPhases.length} phase{selectedPhases.length !== 1 ? 's' : ''} selected. Use the arrows to reorder.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
